/** Projectile helpers for jumps and throws. Pure functions, degrees in, SI units out. */
import { clamp } from './rng.js';

export const G = 9.81;

export interface Flight {
  /** Take-off position and time. */
  x0: number;
  y0: number;
  t0: number;
  vx: number;
  vy: number;
}

export function flightAt(f: Flight, t: number): { x: number; y: number } {
  const dt = Math.max(0, t - f.t0);
  return { x: f.x0 + f.vx * dt, y: f.y0 + f.vy * dt - 0.5 * G * dt * dt };
}

/** Time (from t0) at which the flight comes down to `yLand`; 0 if it never rises above it. */
export function flightLandingTime(f: Flight, yLand: number): number {
  const c = f.y0 - yLand;
  const disc = f.vy * f.vy + 2 * G * c;
  if (disc < 0) return 0;
  return (f.vy + Math.sqrt(disc)) / G;
}

/** Horizontal distance from take-off to landing at ground level. */
export function flightRange(f: Flight, yLand = 0): number {
  return f.vx * flightLandingTime(f, yLand);
}

const toRad = (deg: number) => (deg * Math.PI) / 180;

/**
 * Long jump take-off. Real jumpers cannot convert run-up speed into a 45° launch; the
 * steeper the take-off, the more speed is lost, which puts the best angle in the low
 * twenties. At 10.6 m/s and 25° this gives ~8.1 m (Daley's 8.01).
 */
export const LJ_IDEAL_ANGLE = 25;
export function longJumpTakeoff(v: number, angleDeg: number): { vx: number; vy: number } {
  const a = clamp(angleDeg, 0, 90);
  const retained = 1 - 0.9 * (Math.max(0, a - 22) / 68);
  const s = v * retained;
  return { vx: s * Math.cos(toRad(a)), vy: s * Math.sin(toRad(a)) };
}

/**
 * High jump take-off. Steeper is better up to about 55°, then speed is lost. Vertical
 * conversion is imperfect (0.78). At 8.5 m/s and 55° the centre of mass peaks ~2.35 m.
 */
export const HJ_IDEAL_ANGLE = 55;
export function highJumpTakeoff(v: number, angleDeg: number): { vx: number; vy: number } {
  const a = clamp(angleDeg, 0, 90);
  const retained = 1 - 0.6 * (Math.max(0, a - 55) / 35);
  const s = v * retained;
  return { vx: s * Math.cos(toRad(a)), vy: s * Math.sin(toRad(a)) * 0.78 };
}
